// Общее состояние страницы: сохранённые документы и расчёты, загрузки, чаты, поиск в боковой панели.
// Боковая панель, чат и «Мои документы» читают одни и те же данные и обновляются вместе.
import { api, ApiError, downloadDocument, getJson, postJson, saveBlob, saveResponse } from "./api";
import { boot } from "./boot";
import { Store } from "./lib/reactive";
import { plural, shortDate } from "./lib/text";
import { navigate } from "./router";

export { Store, useStore } from "./lib/reactive";

const alertError = (error: unknown) => alert(error instanceof Error ? error.message : String(error));

// ---------------------------------------------------------------- сохранённые документы и расчёты

export type SavedItem = {
  id: string; kind: "doc" | "calc"; title: string; meta: string; created_at: string; folder_id?: string | null; starred?: boolean;
  payload: Record<string, any>;   // doc: {slug, answers, who}; calc: результат расчёта
};
type SavedLists = { doc: SavedItem[]; calc: SavedItem[] };

export const savedStore = new Store<SavedLists>({ doc: [], calc: [] });
let savedReady: Promise<void> = Promise.resolve();
export const whenSavedReady = () => savedReady;
export const itemMeta = (item: SavedItem) => [item.meta, shortDate(item.created_at)].filter(Boolean).join(" · ");

const OLD_KEYS = { doc: "pravosvod.docs", calc: "pravosvod.calcs" };  // так хранилось раньше — только в браузере

// Документы и расчёты, сохранённые до появления аккаунтов, один раз переносим в аккаунт.
async function importOldItems(): Promise<boolean> {
  const items: Omit<SavedItem, "id">[] = [];
  try {
    for (const d of JSON.parse(localStorage.getItem(OLD_KEYS.doc) || "[]")) {
      items.push({ kind: "doc", title: d.title, meta: d.who || "", created_at: new Date(d.date).toISOString(),
        payload: { slug: d.slug, answers: d.answers, who: d.who || "" } });
    }
    for (const c of JSON.parse(localStorage.getItem(OLD_KEYS.calc) || "[]")) {
      const { id: _id, date, ...calc } = c;
      items.push({ kind: "calc", title: c.title, meta: c.to_pay, created_at: new Date(date).toISOString(), payload: calc });
    }
  } catch {
    return false;
  }
  if (!items.length) return false;
  try {
    await api("/api/saved/import", { items });
    for (const key of Object.values(OLD_KEYS)) localStorage.removeItem(key);
    return true;
  } catch { return false; /* перенесём при следующем заходе */ }
}

// Списки боковой панели приходят вместе со страницей (boot.lists); запрос к серверу — только если их там нет.
export function loadSaved() {
  if (boot.lists) savedStore.set(boot.lists.saved);
  savedReady = (async () => {
    const imported = await importOldItems();
    if (boot.lists && !imported) return;
    try { savedStore.set(await getJson<SavedLists>("/api/saved")); } catch { /* без списка сайт тоже работает */ }
  })();
  return savedReady;
}

export async function addSaved(kind: SavedItem["kind"], title: string, meta: string, payload: Record<string, unknown>) {
  const item = await postJson<SavedItem>("/api/saved", { kind, title, meta, payload });
  savedStore.update((lists) => ({ ...lists, [kind]: [item, ...lists[kind]] }));
  return item;
}

export async function removeSaved(item: SavedItem) {
  if (!confirm(`Удалить «${item.title}» из сохранённого?`)) return;
  try {
    await api(`/api/saved/${item.id}`, undefined, "DELETE");
    savedStore.update((lists) => ({ ...lists, [item.kind]: lists[item.kind].filter((other) => other.id !== item.id) }));
  } catch (error) {
    alertError(error);
  }
}

// ---------------------------------------------------------------- загруженные документы

export type FoundValue = { value: string; quote: string; note?: string; label?: string };
export type UploadedDoc = {
  id: string; filename: string; size: number; kind: string; kind_title: string; title: string; found: number;
  template: string | null; template_title: string | null; created_at: string; fields?: Record<string, FoundValue>;
  folder_id?: string | null;
  starred?: boolean;
  ext?: string;
  preview?: string;   // начало текста — для плитки
};

export const uploadedStore = new Store<UploadedDoc[]>([]);
let uploadedReady: Promise<void> = Promise.resolve();
export const whenUploadedReady = () => uploadedReady;

export const docName = (doc: UploadedDoc) => (doc.kind === "unknown" ? doc.filename : doc.title);
export const foundText = (n: number) => `найдено ${plural(n, "значение", "значения", "значений")}`;
export const docMeta = (doc: UploadedDoc) =>
  [doc.kind === "unknown" ? "Готов к разбору" : foundText(doc.found), shortDate(doc.created_at)].join(" · ");

export function loadUploaded() {
  if (boot.lists) {
    uploadedStore.set(boot.lists.documents);
    return uploadedReady;
  }
  uploadedReady = (async () => {
    try { uploadedStore.set(await getJson<UploadedDoc[]>("/api/documents")); } catch { /* без списка сайт тоже работает */ }
  })();
  return uploadedReady;
}

const MAX_UPLOAD = 10 * 1024 * 1024;

const UPLOAD_FAILED = "Не получилось загрузить файл. Попробуйте ещё раз.";
const TOO_BIG = "Файл больше 10 МБ. Загрузите документ поменьше.";

// Отправка файла. onProgress — доля отправленного (0…1): после 1 сервер ещё читает документ.
function sendFile(file: File, onProgress?: (share: number) => void): Promise<UploadedDoc> {
  return new Promise((resolve, reject) => {
    const request = new XMLHttpRequest();
    request.open("POST", "/api/documents");
    request.setRequestHeader("Content-Type", "application/octet-stream");
    request.setRequestHeader("X-File-Name", encodeURIComponent(file.name));
    request.upload.onprogress = (event) => { if (event.lengthComputable) onProgress?.(event.loaded / event.total); };
    request.onerror = () => reject(new ApiError(UPLOAD_FAILED, 0));
    request.onload = () => {
      let data: any = null;
      try { data = JSON.parse(request.responseText); } catch { /* ответ не JSON */ }
      if (request.status >= 200 && request.status < 300 && data) return resolve(data);
      reject(new ApiError(data?.detail || (request.status === 413 ? TOO_BIG : UPLOAD_FAILED), request.status));
    };
    request.send(file);
  });
}

export async function uploadFile(file: File, onProgress?: (share: number) => void): Promise<UploadedDoc> {
  if (file.size > MAX_UPLOAD) throw new Error(TOO_BIG);
  const doc = await sendFile(file, onProgress);
  uploadedStore.update((docs) => [doc, ...docs]);
  return doc;
}

export async function removeUploaded(doc: UploadedDoc) {
  if (!confirm(`Удалить «${docName(doc)}»?`)) return;
  try {
    await api(`/api/documents/${doc.id}`, undefined, "DELETE");
    uploadedStore.update((docs) => docs.filter((other) => other.id !== doc.id));
  } catch (error) {
    alertError(error);
  }
}

// ---------------------------------------------------------------- чаты

export type ChatBrief = { id: string; title: string; created_at: string; updated_at: string };

export const chatsStore = new Store<ChatBrief[]>([]);
// Открытый сейчас чат (/?chat=…): подсвечиваем в списке, новые реплики дописываем в него.
export const currentChatStore = new Store<string | null>(new URLSearchParams(location.search).get("chat"));

export async function loadChats() {
  if (boot.lists) return chatsStore.set(boot.lists.chats);
  try { chatsStore.set(await getJson<ChatBrief[]>("/api/chats")); } catch { /* без списка сайт тоже работает */ }
}

export const upsertChat = (chat: ChatBrief) => chatsStore.update((chats) => [chat, ...chats.filter((other) => other.id !== chat.id)]);

// Переписки чатов в памяти страницы: чат из панели открывается сразу, без запроса к серверу (до него 180–600 мс).
// Запись годится, пока не изменился чат в списке (updated_at), иначе переписку берём с сервера заново.
export type ChatFull = ChatBrief & { messages: { role: string; text: string }[] };
const chatCache = new Map<string, ChatFull>();
const chatLoading = new Map<string, Promise<ChatFull>>();

export function rememberChat(chat: ChatFull) {
  chatCache.set(chat.id, chat);
}

export function cachedChat(id: string): ChatFull | undefined {
  const chat = chatCache.get(id);
  const brief = chatsStore.get().find((other) => other.id === id);
  return chat && (!brief || brief.updated_at === chat.updated_at) ? chat : undefined;
}

export function loadChat(id: string): Promise<ChatFull> {
  const cached = cachedChat(id);
  if (cached) return Promise.resolve(cached);
  let loading = chatLoading.get(id);
  if (!loading) {
    loading = getJson<ChatFull>(`/api/chats/${id}`).then((chat) => { rememberChat(chat); return chat; })
      .finally(() => chatLoading.delete(id));
    chatLoading.set(id, loading);
  }
  return loading;
}

// Наведение или касание чата в панели: переписка загружается, пока человек нажимает.
export const prefetchChat = (id: string) => { loadChat(id).catch(() => { /* откроем обычным запросом */ }); };

// После загрузки страницы — переписки последних чатов одним запросом.
export async function prefetchChats() {
  if (!chatsStore.get().length) return;
  try {
    for (const chat of await getJson<ChatFull[]>("/api/chats/recent")) rememberChat(chat);
  } catch { /* без заготовки чаты открываются запросом */ }
}

// Новое название: чат остаётся на своём месте в списке, открытый чат меняет заголовок (controller.tsx).
export async function renameChat(chat: ChatBrief, title: string) {
  try {
    const renamed = await postJson<ChatBrief>(`/api/chats/${chat.id}`, { title }, "PATCH");
    chatsStore.update((chats) => chats.map((other) => (other.id === chat.id ? renamed : other)));
    const cached = chatCache.get(chat.id);
    if (cached) chatCache.set(chat.id, { ...cached, title: renamed.title });
  } catch (error) {
    alertError(error);
  }
}

export async function removeChat(chat: ChatBrief) {
  if (!confirm(`Удалить чат «${chat.title}»?`)) return;
  try {
    await api(`/api/chats/${chat.id}`, undefined, "DELETE");
    chatsStore.update((chats) => chats.filter((other) => other.id !== chat.id));
    chatCache.delete(chat.id);
    if (chat.id === currentChatStore.get()) navigate("/");
  } catch (error) {
    alertError(error);
  }
}

// ---------------------------------------------------------------- папки во вкладке «Документы»

export type Folder = { id: string; name: string; created_at: string };
// Документ перетаскивают в папку: в dataTransfer — ключ «s<id>» (сохранённый) или «u<id>» (загруженный).
export const DOC_DRAG = "application/x-pravosvod-doc";
// Что лежит в папке: сохранённый документ или расчёт (saved) либо загруженный файл (upload).
export type FolderEntry = { kind: "saved"; item: SavedItem } | { kind: "upload"; item: UploadedDoc };

// Новое название документа или расчёта (меню «⋯» → «Переименовать»): у загруженного файла — имя файла
// (расширение сервер сохраняет), у распознанного документа — заголовок, у собранного документа и расчёта — название.
export async function renameEntry(entry: FolderEntry, name: string) {
  try {
    if (entry.kind === "upload") {
      const doc = await postJson<UploadedDoc>(`/api/documents/${entry.item.id}`, { name }, "PATCH");
      uploadedStore.update((docs) => docs.map((other) => (other.id === doc.id ? { ...other, ...doc } : other)));
    } else {
      const item = await postJson<SavedItem>(`/api/saved/${entry.item.id}`, { name }, "PATCH");
      savedStore.update((lists) => ({ ...lists, [item.kind]: lists[item.kind].map((other) => (other.id === item.id ? item : other)) }));
    }
  } catch (error) {
    alertError(error);
  }
}

export const foldersStore = new Store<Folder[]>([]);
const byName = (folders: Folder[]) => [...folders].sort((a, b) => a.name.localeCompare(b.name, "ru") || a.created_at.localeCompare(b.created_at));

export async function loadFolders() {
  if (boot.lists) return foldersStore.set(byName(boot.lists.folders || []));
  try { foldersStore.set(byName(await getJson<Folder[]>("/api/folders"))); } catch { /* без папок список документов тоже работает */ }
}

export async function createFolder(name: string): Promise<Folder | null> {
  try {
    const folder = await postJson<Folder>("/api/folders", { name });
    foldersStore.update((folders) => byName([...folders, folder]));
    return folder;
  } catch (error) {
    alertError(error);
    return null;
  }
}

export async function renameFolder(folder: Folder, name: string) {
  try {
    const renamed = await postJson<Folder>(`/api/folders/${folder.id}`, { name }, "PUT");
    foldersStore.update((folders) => byName(folders.map((other) => (other.id === folder.id ? renamed : other))));
  } catch (error) {
    alertError(error);
  }
}

// В чьём списке лежит документ и как поменять его папку на экране.
function setFolder(entry: FolderEntry, folderId: string | null) {
  if (entry.kind === "saved") {
    const kind = entry.item.kind;
    savedStore.update((lists) => ({ ...lists, [kind]: lists[kind].map((item) => (item.id === entry.item.id ? { ...item, folder_id: folderId } : item)) }));
  } else {
    uploadedStore.update((docs) => docs.map((doc) => (doc.id === entry.item.id ? { ...doc, folder_id: folderId } : doc)));
  }
}

export async function removeFolder(folder: Folder) {
  if (!confirm(`Удалить папку «${folder.name}»? Документы из неё не удалятся — они вернутся в общий список.`)) return;
  try {
    await api(`/api/folders/${folder.id}`, undefined, "DELETE");
    foldersStore.update((folders) => folders.filter((other) => other.id !== folder.id));
    savedStore.update((lists) => ({
      doc: lists.doc.map((item) => (item.folder_id === folder.id ? { ...item, folder_id: null } : item)),
      calc: lists.calc.map((item) => (item.folder_id === folder.id ? { ...item, folder_id: null } : item)),
    }));
    uploadedStore.update((docs) => docs.map((doc) => (doc.folder_id === folder.id ? { ...doc, folder_id: null } : doc)));
  } catch (error) {
    alertError(error);
  }
}

// Переложить документ: на экране — сразу, на сервере — следом; не получилось — возвращаем как было.
export async function moveToFolder(entry: FolderEntry, folderId: string | null) {
  const before = entry.item.folder_id || null;
  if (before === folderId) return;
  setFolder(entry, folderId);
  try {
    await api("/api/folders/move", { kind: entry.kind, id: entry.item.id, folder_id: folderId });
  } catch (error) {
    setFolder(entry, before);
    alertError(error);
  }
}

// ---------------------------------------------------------------- несколько документов сразу (pravosvod/library.py)

const refOf = (entry: FolderEntry) => ({ kind: entry.kind, id: entry.item.id });
const keysOf = (entries: FolderEntry[]) => new Set(entries.map((entry) => `${entry.kind}:${entry.item.id}`));

// Поменять поля у документов на экране: и в сохранённых, и в загруженных.
function patchEntries(entries: FolderEntry[], patch: Partial<{ folder_id: string | null; starred: boolean }>) {
  const keys = keysOf(entries);
  const touch = <T extends { id: string }>(kind: string) => (item: T) => (keys.has(`${kind}:${item.id}`) ? { ...item, ...patch } : item);
  savedStore.update((lists) => ({ doc: lists.doc.map(touch("saved")), calc: lists.calc.map(touch("saved")) }));
  uploadedStore.update((docs) => docs.map(touch("upload")));
}

async function bulk(action: "move" | "delete" | "star", entries: FolderEntry[], extra: Record<string, unknown> = {}) {
  await api("/api/library/bulk", { action, items: entries.map(refOf), ...extra });
}

export async function moveMany(entries: FolderEntry[], folderId: string | null) {
  try {
    await bulk("move", entries, { folder_id: folderId });
    patchEntries(entries, { folder_id: folderId });
  } catch (error) {
    alertError(error);
  }
}

// Избранное: звёздочка ставится сразу, сервер — следом; не получилось — возвращаем как было.
export async function starMany(entries: FolderEntry[], starred: boolean) {
  const before = entries.map((entry) => ({ entry, starred: Boolean(entry.item.starred) }));
  patchEntries(entries, { starred });
  try {
    await bulk("star", entries, { starred });
  } catch (error) {
    for (const { entry, starred: was } of before) patchEntries([entry], { starred: was });
    alertError(error);
  }
}

export async function deleteMany(entries: FolderEntry[]): Promise<boolean> {
  if (!confirm(entries.length === 1 ? `Удалить «${entries[0].kind === "upload" ? docName(entries[0].item) : entries[0].item.title}»?`
    : `Удалить выбранные документы (${entries.length})? Отменить это нельзя.`)) return false;
  try {
    await bulk("delete", entries);
    const keys = keysOf(entries);
    savedStore.update((lists) => ({ doc: lists.doc.filter((item) => !keys.has(`saved:${item.id}`)),
      calc: lists.calc.filter((item) => !keys.has(`saved:${item.id}`)) }));
    uploadedStore.update((docs) => docs.filter((doc) => !keys.has(`upload:${doc.id}`)));
    return true;
  } catch (error) {
    alertError(error);
    return false;
  }
}

function calcText(item: SavedItem): string {
  const calc = item.payload;
  return [`Расчёт госпошлины: ${item.title}`, "", `К оплате: ${calc.to_pay || ""}`, `Что подаёте: ${calc.what || ""}`,
    `Расчёт: ${calc.formula || ""}`, `Норма: ${calc.norm || ""} (${calc.edition || ""})`, "",
    "Посчитано в Правосводе (pravosvod.ru). Льготы по ст. 333.36 и 333.37 НК РФ не учтены.", ""].join("\n");
}

// Скачать: один документ — как есть (оригинал, Word или расчёт текстом), несколько — одним архивом .zip.
export async function downloadMany(entries: FolderEntry[]) {
  try {
    if (entries.length === 1) {
      const entry = entries[0];
      if (entry.kind === "upload") {
        const link = document.createElement("a");
        link.href = `/api/documents/${entry.item.id}/file`;
        link.download = "";
        document.body.append(link);
        link.click();
        link.remove();
      } else if (entry.item.kind === "calc") {
        saveBlob(new Blob([calcText(entry.item)], { type: "text/plain;charset=utf-8" }), `${entry.item.title}.txt`);
      } else {
        await downloadDocument(entry.item.payload.slug, entry.item.payload.answers);
      }
      return;
    }
    await saveResponse(await api("/api/library/zip", { items: entries.map(refOf) }), "Документы.zip");
  } catch (error) {
    alertError(error);
  }
}

// ---------------------------------------------------------------- поиск в боковой панели

export const searchStore = new Store("");
export const searchQuery = () => searchStore.get().trim().toLocaleLowerCase("ru");

// ---------------------------------------------------------------- окна

export type SourceRef = { label: string; quote: string; source_id: string };
export const loginStore = new Store<{ open: number; reason: string }>({ open: 0, reason: "" });
export const openLogin = (reason = "") => {
  document.body.classList.remove("nav-open");
  loginStore.set({ open: loginStore.get().open + 1, reason });
};

// «Сначала гость, потом просим войти»: после первого сохранённого документа предлагаем вход один раз.
export function promptLogin(isGuest: boolean, reason: string) {
  if (!isGuest) return;
  try {
    if (localStorage.getItem("pravosvod.loginPrompted")) return;
    localStorage.setItem("pravosvod.loginPrompted", "1");
  } catch { /* без хранилища просто покажем окно */ }
  openLogin(reason);
}

// Расчёт, открытый из боковой панели: на главной показывает сам чат, на других страницах — переход.
export const calcOpener: { open: ((item: SavedItem) => void) | null } = { open: null };
export function openCalc(item: SavedItem) {
  document.body.classList.remove("nav-open");
  if (calcOpener.open) calcOpener.open(item);
  else navigate(`/?start=calc:${item.id}`);
}
